/**
 * Single source of truth for TRAMS travel-type knowledge, shared by the NestJS
 * server and the React client.
 *
 * Consolidates what used to be duplicated (and drifting) across:
 *   - server: invoice.service.ts, agent-rate-resolver.ts, trams-accounts.service.ts
 *   - client: travelType.ts, InvoiceFormCard.tsx, unclaimedTravelType.ts,
 *     NewInvoiceStep.tsx
 *
 * Canonical labels use a spaced EN-DASH (U+2013, "Ship – Final"). TRAMS / the
 * Middle Layer emit plain hyphens ("Ship - Final") or no dash at all
 * ("Ship Final"); `canonicalizeTravelTypeLabel` folds all of those variants to
 * the canonical label.
 */

/** Authoritative TRAMS TRAVELTYPENO → canonical label (16 types). */
export const TRAVEL_TYPE_BY_LINKNO: Record<number, string> = {
  1: 'Domestic Air',
  2: 'Hotel',
  3: 'Car',
  4: 'Ship – Deposit',
  5: 'Ship – Final',
  6: 'Rail',
  7: 'Insurance',
  8: 'Intl Air',
  9: 'Tour – Deposit',
  10: 'Tour – Final',
  11: 'Service Fee',
  12: 'Misc',
  13: 'Ancillary Air',
  15: 'Bus & Limo',
  17: 'Non ARC Air',
  19: 'Southwest',
};

/** Travel types a user may pick in the invoice form (UI dropdown). */
export const SELECTABLE_TRAVEL_TYPES = [
  'Hotel',
  'Car',
  'Ship – Deposit',
  'Ship – Final',
  'Rail',
  'Insurance',
  'Tour – Deposit',
  'Tour – Final',
  'Bus & Limo',
] as const;

/** Travel-type link nos whose commission uses the advisor's AIR rate. */
export const AIR_TRAVEL_TYPE_LINK_NOS: readonly number[] = [1, 8, 13, 17, 19];

/** Travel-type link no whose commission uses the advisor's SERVICE FEE rate. */
export const SERVICE_FEE_TRAVEL_TYPE_LINK_NO = 11;

/**
 * Fallback link no for unknown/unmapped travel types. Misc(12) is a safer
 * neutral bucket than mislabeling as Hotel(2).
 */
export const DEFAULT_TRAVELTYPE_LINKNO = 12; // Misc

/**
 * 1:1 alias → canonical label. Covers short codes ("Ship-Finl"), no-dash forms
 * ("Ship Final") and display aliases ("Cruise", "Tour"). Only unambiguous
 * synonyms belong here; non-1:1 legacy fallbacks (e.g. "Dom. Air") live in
 * {@link LEGACY_LINKNO_BY_LABEL} instead.
 */
const TRAVEL_TYPE_ALIAS_TO_CANONICAL: Record<string, string> = {
  'Ship-Dpst': 'Ship – Deposit',
  'Ship-Finl': 'Ship – Final',
  'Tour-Dpst': 'Tour – Deposit',
  'Tour-Finl': 'Tour – Final',
  'Ship Deposit': 'Ship – Deposit',
  'Ship Final': 'Ship – Final',
  'Tour Deposit': 'Tour – Deposit',
  'Tour Final': 'Tour – Final',
  Cruise: 'Ship – Final',
  Tour: 'Tour – Final',
};

/**
 * Normalize a stored travel-type string to its canonical label.
 *
 * Steps: (1) collapse a spaced hyphen/em-dash to a spaced en-dash, then
 * (2) apply the 1:1 alias map. Aliases with no surrounding-space dash (e.g.
 * "Ship-Finl", "Ship Final", "Cruise") are handled purely by the alias map, so
 * a bare hyphen inside a short code is never mangled.
 */
export function canonicalizeTravelTypeLabel(raw: string): string {
  const spaced = raw.replace(/\s[-–—]\s/g, ' – ').trim();
  return TRAVEL_TYPE_ALIAS_TO_CANONICAL[spaced] ?? spaced;
}

/** canonical label → link no (derived from {@link TRAVEL_TYPE_BY_LINKNO}). */
const LINKNO_BY_LABEL: Record<string, number> = Object.fromEntries(
  Object.entries(TRAVEL_TYPE_BY_LINKNO).map(([linkNo, label]) => [label, Number(linkNo)]),
);

/**
 * Legacy, non-1:1 label spellings kept only for back-compat linkno resolution
 * (intentionally excluded from {@link TRAVEL_TYPE_ALIAS_TO_CANONICAL} so they
 * are never used to rewrite a persisted label).
 */
const LEGACY_LINKNO_BY_LABEL: Record<string, number> = {
  'Dom. Air': 1,
  'Misc.': 12,
};

/** Resolve a travel-type label (any variant) to its TRAMS link no. */
export function travelTypeLabelToLinkNo(
  raw: string | null | undefined,
): number | undefined {
  if (!raw) return undefined;
  const canonical = canonicalizeTravelTypeLabel(raw);
  return (
    LINKNO_BY_LABEL[canonical] ??
    LEGACY_LINKNO_BY_LABEL[canonical] ??
    LEGACY_LINKNO_BY_LABEL[raw]
  );
}

/** Resolve a TRAMS link no to its canonical label. */
export function linkNoToTravelTypeLabel(
  linkNo: number | null | undefined,
): string | undefined {
  if (linkNo == null) return undefined;
  return TRAVEL_TYPE_BY_LINKNO[linkNo];
}

/** Whether a label (any variant) is a known travel type. */
export function isKnownTravelTypeLabel(raw: string | null | undefined): boolean {
  return travelTypeLabelToLinkNo(raw) !== undefined;
}

/**
 * Whether a stored travel type is user-selectable. Non-selectable types (e.g.
 * Domestic Air, Intl Air, Service Fee, Misc, Ancillary Air, Non ARC Air,
 * Southwest) render read-only in the invoice form.
 */
export function isSelectableTravelType(raw: string): boolean {
  return (SELECTABLE_TRAVEL_TYPES as readonly string[]).includes(
    canonicalizeTravelTypeLabel(raw),
  );
}

/**
 * Whether a stored travel type must render read-only in the invoice form: a
 * label TRAMS owns but the user may not pick (Domestic Air, Intl Air, Service
 * Fee, Misc, Ancillary Air, Non ARC Air, Southwest).
 *
 * Deliberately narrower than `!isSelectableTravelType`: an *unknown* persisted
 * label (bad import, retired TRAMS type) is not read-only, so the advisor can
 * replace it with a supported label via the selector.
 */
export function isReadOnlyTravelType(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return isKnownTravelTypeLabel(raw) && !isSelectableTravelType(raw);
}

/** Display-only label overrides (stored value stays canonical). */
const TRAVEL_TYPE_DISPLAY_OVERRIDE: Record<string, string> = {
  'Ship – Final': 'Cruise',
  'Tour – Final': 'Tour',
};

/**
 * UI display label for a travel type. Canonicalizes first, then applies the
 * Cruise/Tour display overrides. Returns the (canonicalized) input unchanged
 * when there is no override.
 */
export function travelTypeDisplayLabel(raw: string): string {
  const canonical = canonicalizeTravelTypeLabel(raw);
  return TRAVEL_TYPE_DISPLAY_OVERRIDE[canonical] ?? canonical;
}
